"use client";
import { FacetCounts } from "@/apiUtils/algoliaUtils/types";
import { getCurrencyByLocale, decimalSeparatedPrice } from "@/apiUtils/commonUtils";
import CurrencyIcon from "@/common/core/icons/CurrencyIcon";
import Range from "@/components/ui/range";
import TextField from "@/components/ui/textField/TextField";
// import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/tailwind";
import { getLangFromLocale } from "@/utils/common";
import { useParams, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface IProps {
  min: number;
  max: number;
  priceCounts: FacetCounts;
  onChange: (key: string, value: string) => void;
}

const calculateStyleProperty = (value: number, min: number, max: number): string => {
  const clampedValue = Math.min(Math.max(value, min), max);
  const percentage = ((clampedValue - min) / (max - min)) * 100;
  return percentage + "%";
};

const Price = ({ min, max, priceCounts, onChange }: IProps) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const initialRenderRef = useRef(true);

  const currency = useMemo(() => getCurrencyByLocale(params?.locale as string), [params.locale]);
  const lang = useMemo(() => getLangFromLocale(params.locale as string), [params.locale]);
  const isRTL = lang === "ar";

  // Retrieve the keys (prices) from the priceData object and sort them
  const keys = useMemo(
    () =>
      Object.keys(priceCounts)
        .map(parseFloat)
        .sort((a, b) => a - b),
    [],
  );

  const urlPMin = searchParams?.get("pmin");
  const urlPMax = searchParams?.get("pmax");

  const pMin = urlPMin ? parseInt(urlPMin) : min;
  const pMax = urlPMax ? parseInt(urlPMax) : max;

  const [range, setRange] = useState([pMin, pMax]);
  const [fieldRange, setFieldRange] = useState([pMin, pMax]);
  // const debouncedRange = useDebounce(range, 500);

  const handleSliderChange = (value: number | number[]): void => {
    initialRenderRef.current = false;
    if (Array.isArray(value)) {
      setRange(value);
      setFieldRange(value);
    }
  };

  const handleSliderComplete = (value: number | number[]): void => {
    if (Array.isArray(value)) {
      // Filter items within the specified range
      const itemsWithinRange = keys.filter((val) => val >= value[0] && val <= value[1]);

      let isValidRange = true;
      if (isNaN(value[0]) || isNaN(value[1])) isValidRange = false;
      if (value[0] < min || value[0] > value[1]) isValidRange = false;
      if (value[1] > max || value[1] < value[0]) value[1] = max;

      // If no items are within the range, adjust the range to the nearest values
      if (!itemsWithinRange.length || !isValidRange) {
        let nearestMin = min;
        let nearestMax = max;

        // Find the nearest minimum and maximum values within the available keys
        for (let key of keys) {
          if (key <= value[0]) nearestMin = key;
          if (key >= value[1]) {
            nearestMax = key;
            break;
          }
        }

        // Update the range values and refine the price
        setRange([nearestMin, nearestMax]);
        setFieldRange([nearestMin, nearestMax]);
        refinePrice(nearestMin, nearestMax);
      } else {
        // If items are within the range, update the range values and refine the price
        setRange(value);
        setFieldRange(value);
        refinePrice(value[0], value[1]);
      }
    }
  };

  const handleChangePrice = (index: number, value: string) => {
    const newPrice = parseInt(value.replace(/\D/g, ""));
    const tempRange = [...range];
    tempRange[index] = newPrice;
    setFieldRange(tempRange);
    if (isNaN(newPrice)) return;
    // if (index === 0 && (newPrice < min || newPrice > range[1])) return;
    // if (index === 1 && (newPrice > max || newPrice < range[0])) return;
    setRange(tempRange);
  };

  const handleBlur = () => {
    handleSliderComplete(fieldRange);
  };

  const formatPrice = useCallback(
    (price: string | number) => {
      const numericPrice = typeof price === "string" ? parseFloat(price) : price;
      if (isNaN(numericPrice)) return "0";
      return decimalSeparatedPrice(numericPrice).split(".")[0];
    },
    [],
  );

  const refinePrice = (pmin: number, pmax: number) => {
    if (pmin == min && pmax == max) {
      onChange("pmin", "");
      onChange("pmax", "");
    } else {
      onChange("pmin", pmin.toString());
      onChange("pmax", pmax.toString());
    }
  };

  useEffect(() => {
    if (!urlPMax && !urlPMin) {
      setRange([min, max]);
      setFieldRange([min, max]);
    }
  }, [searchParams]);

  const calculateStylePercentage = (value: number): string => {
    return calculateStyleProperty(value, min || 0, max || 0);
  };

  const getLabelStyle = (value: number, isMin: boolean) => {
    const position = calculateStylePercentage(value);
    const percentage = parseInt(position);

    if (isRTL) {
      if (percentage >= 95 && !isMin) {
        return {
          right: "100%",
          left: "auto",
          transform: "translateX(100%)",
          textAlign: "right" as const,
        };
      } else if (percentage <= 5 && isMin) {
        return {
          right: "0%",
          left: "auto",
          transform: "translateX(0%)",
          textAlign: "left" as const,
        };
      } else {
        return {
          right: position,
          left: "auto",
          transform: "translateX(50%)",
        };
      }
    } else {
      if (percentage <= 5 && isMin) {
        return {
          left: "0%",
          right: "auto",
          transform: "translateX(0%)",
          textAlign: "left" as const,
        };
      } else if (percentage >= 95 && !isMin) {
        return {
          left: "100%",
          right: "auto",
          transform: "translateX(-100%)",
          textAlign: "right" as const,
        };
      } else {
        return {
          left: position,
          right: "auto",
          transform: "translateX(-50%)",
        };
      }
    }
  };

  return (
    <>
      <div className="flex gap-4 p-4">
        <TextField
          type="text"
          variant="outlined"
          className={cn(
            "appearance-none bg-white ltr:pl-16 rtl:pr-16",
            "[&::-moz-number-spin-box]:hidden [&::-webkit-inner-spin-button]:appearance-none",
            "[&::-webkit-outer-spin-button]:appearance-none",
          )}
          value={formatPrice(fieldRange[0])}
          placeholder={formatPrice(min)}
          onChange={(e) => handleChangePrice(0, e.target.value)}
          onBlur={handleBlur}
          slots={{
            startAction: {
              action: (
                <span className="ps-2 text-sm">
                  <CurrencyIcon currency={currency} />
                </span>
              ),
            },
            error: {
              variant: "transparent",
              error: isNaN(fieldRange[0]) || fieldRange[0] < min || fieldRange[0] > fieldRange[1],
            },
          }}
        />
        <TextField
          type="text"
          variant="outlined"
          className={cn(
            "appearance-none bg-white ltr:pl-16 rtl:pr-16",
            "[&::-moz-number-spin-box]:hidden [&::-webkit-inner-spin-button]:appearance-none",
            "[&::-webkit-outer-spin-button]:appearance-none",
          )}
          value={formatPrice(fieldRange[1])}
          placeholder={formatPrice(max)}
          onChange={(e) => handleChangePrice(1, e.target.value)}
          onBlur={handleBlur}
          slots={{
            startAction: {
              action: (
                <span className="ps-2 text-sm">
                  <CurrencyIcon currency={currency} />
                </span>
              ),
            },
            error: {
              variant: "transparent",
              error: isNaN(fieldRange[1]) || fieldRange[1] > max || fieldRange[1] < fieldRange[0],
            },
          }}
        />
      </div>

      <div className="relative mx-8 h-10">
        <Range
          min={min}
          max={max}
          value={[range[0], range[1]]}
          onChange={handleSliderChange}
          onChangeComplete={handleSliderComplete}
          range
          allowCross={false}
          reverse={isRTL}
        />
        <div className="absolute" style={getLabelStyle(range[0], true)}>
          <div className="">{formatPrice(range[0])}</div>
        </div>
        <div className="absolute" style={getLabelStyle(range[1], false)}>
          <div className="">{formatPrice(fieldRange[1])}</div>
        </div>
      </div>
    </>
  );
};

export default Price;
